import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Layout, Modal, Spin } from "antd";
import { globalModel } from "../../global/GlobalModel";
import { useRelatedSpacesOrdersModals } from "../shell/RelatedSpacesOrdersModalsProvider";
import { SpaceModel, isValidSection, resolveNavItemForSection } from "./SpaceModel";
import { SpaceHomePage } from "./home/SpaceHomePage";
import type { MembershipProduct } from "./SpaceType";
import {
  siteNavUserAvatarCssVars,
  sitePageBackgroundStyle,
  spaceProductShellStyle,
} from "../../SiteConfig";
import { SpaceHeader } from "./components/SpaceHeader";
import { SpaceProductSection } from "./components/SpaceProductSection";
import { PageLayoutBridge } from "../../layout/PageLayoutContext";
import { UserNotesEntitlementProvider } from "../../components/fin-web/UserNotesEntitlementContext";
import "../shell/ShellTopBar.css";
import "./SpaceStyle.css";

const { Header, Content } = Layout;

export const SpaceContent = observer(
  (props: { spaceId: string; section: string | undefined }) => {
    const { spaceId, section } = props;
    const navigate = useNavigate();
    const model = useMemo(() => new SpaceModel(), []);
    const shellModals = useRelatedSpacesOrdersModals();
    const membershipListRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
      model.setSpaceId(spaceId);
      void model.loadConfig();
    }, [spaceId, model]);

    useEffect(() => {
      if (!model.loaded || !model.spaceModules.length) {
        return;
      }
      void model.refreshSubscriptionMerge();
    }, [spaceId, model, model.loaded, globalModel.isLoggedIn]);

    useEffect(() => {
      if (!globalModel.isLoggedIn) {
        model.resetFavorState();
        return;
      }
      void model.loadFavorState();
    }, [spaceId, globalModel.isLoggedIn, model]);

    const onSubscribeScroll = useCallback(() => {
      membershipListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const onFollowClick = useCallback(() => {
      void model.submitFavor(true);
    }, [model]);

    const onRequestUnfollow = useCallback(() => {
      Modal.confirm({
        title: "取消关注",
        content: "确定取消关注该空间？",
        okText: "确认",
        cancelText: "取消",
        width: 400,
        centered: true,
        onOk: () => model.submitFavor(false),
      });
    }, [model]);

    const sectionSafe = section ?? "home";

    const resolvedNavForSection = useMemo(() => {
      if (!model.loaded || sectionSafe === "home") {
        return null;
      }
      return resolveNavItemForSection(
        sectionSafe,
        model.navItems,
        model.membershipProducts
      );
    }, [model.loaded, sectionSafe, model.navItems, model.membershipProducts]);

    const selectedKey: string =
      model.navItems.length > 0 && isValidSection(sectionSafe, model.navItems)
        ? sectionSafe === "home"
          ? "home"
          : resolvedNavForSection
            ? resolvedNavForSection.routeKey
            : "home"
        : model.loaded
          ? "home"
          : sectionSafe;

    const moduleForActiveSection = useMemo((): MembershipProduct | null => {
      if (sectionSafe === "home" || !model.loaded) {
        return null;
      }
      if (!isValidSection(sectionSafe, model.navItems)) {
        return null;
      }
      const nav = resolveNavItemForSection(
        sectionSafe,
        model.navItems,
        model.membershipProducts
      );
      if (!nav?.spaceProductId) {
        return null;
      }
      return (
        model.membershipProducts.find(
          (m) => m.spaceProductId === nav.spaceProductId
        ) ?? null
      );
    }, [sectionSafe, model.loaded, model.navItems, model.membershipProducts]);

    useEffect(() => {
      if (!model.loaded || sectionSafe === "home") {
        return;
      }
      const nav = resolveNavItemForSection(
        sectionSafe,
        model.navItems,
        model.membershipProducts
      );
      if (!nav || sectionSafe === nav.routeKey) {
        return;
      }
      navigate(
        `/space/${spaceId}/${encodeURIComponent(nav.routeKey)}`,
        { replace: true }
      );
    }, [
      model.loaded,
      sectionSafe,
      model.navItems,
      model.membershipProducts,
      spaceId,
      navigate,
    ]);

    useEffect(() => {
      const sec = section ?? "home";
      if (
        !model.loaded ||
        model.navItems.length === 0 ||
        isValidSection(sec, model.navItems)
      ) {
        return;
      }
      if (
        model.spaceModules.length > 0 &&
        model.membershipProducts.length === 0
      ) {
        return;
      }
      navigate(`/space/${spaceId}/home`, { replace: true });
    }, [
      model.loaded,
      model.navItems,
      model.membershipProducts,
      model.spaceModules.length,
      section,
      spaceId,
      navigate,
    ]);

    const spacePresenceName = model.spaceProfile?.name?.trim() || spaceId;

    if (model.loaded && !model.spaceProfile) {
      return <Navigate to="/" replace />;
    }

    return (
      <UserNotesEntitlementProvider membershipProducts={model.membershipProducts}>
        <>
          <PageLayoutBridge
          productKind={
            sectionSafe === "home" || !moduleForActiveSection?.userSubscribed
              ? undefined
              : moduleForActiveSection.productKind
          }
        />
        <Layout className="space-root" style={sitePageBackgroundStyle("space")}>
          <Header className="space-header" style={siteNavUserAvatarCssVars()}>
            <SpaceHeader
              spaceId={spaceId}
              spacePresenceName={spacePresenceName}
              loaded={model.loaded}
              navItems={model.navItems}
              selectedKey={selectedKey}
              onLoginRequest={() => globalModel.openLoginModal()}
              onFollowsClick={() =>
                shellModals.openRelatedSpaces({ initialTab: "follow" })
              }
            />
          </Header>
          <Content className="space-content">
            <div className="space-content-inner">
              {model.configLoadError && (
                <p className="space-config-warn" role="status">
                  会员与导航配置未加载：{model.configLoadError}（已显示空间首页）
                </p>
              )}
              <Spin spinning={!model.loaded}>
                {sectionSafe === "home" ? (
                  <SpaceHomePage
                    spaceId={spaceId}
                    spaceProfile={model.spaceProfile}
                    homeData={model.homeData}
                    membershipProducts={model.membershipProducts}
                    isLoggedIn={globalModel.isLoggedIn}
                    favorStatus={model.favorStatus}
                    membershipListRef={membershipListRef}
                    onLoginRequest={() => globalModel.openLoginModal()}
                    onFollow={onFollowClick}
                    onRequestUnfollow={onRequestUnfollow}
                    onSubscribeScroll={onSubscribeScroll}
                  />
                ) : (
                  <div
                    className="space-product-shell"
                    style={spaceProductShellStyle()}
                  >
                    <SpaceProductSection
                      spaceId={spaceId}
                      sectionKey={sectionSafe}
                      module={moduleForActiveSection}
                      membershipProducts={model.membershipProducts}
                      spaceProfile={model.spaceProfile}
                      isLoggedIn={globalModel.isLoggedIn}
                      onLoginRequest={() => globalModel.openLoginModal()}
                    />
                  </div>
                )}
              </Spin>
            </div>
          </Content>
        </Layout>
        </>
      </UserNotesEntitlementProvider>
    );
  }
);
